import React from "react";
import { useTranslation } from "react-i18next";
import { FileDown, Lock, Check, ArrowRight } from "lucide-react";
import { apiService } from "../../services";
import { Modal } from "../common";
import { AuthProviders } from "../auth";
import {
  courseDecks,
  coursePartOrder,
  courseTotals,
  type CourseDeck,
} from "../../data/course";
import { hasCourseAccess } from "./access";

interface CourseLibraryProps {
  user: { userID?: string } | null;
  /** True while Firebase is still restoring a persisted session. */
  authLoading?: boolean;
}

/**
 * Why a deck isn't available.
 *
 * The decks are still being revised, so this page carries no purchase path:
 * a visitor who is signed out is offered sign-in, and a signed-in customer
 * without the entitlement simply sees the deck as locked. (The purchase and
 * upgrade flows live in git history if this page is ever promoted.)
 */
type Gate = "open" | "signin" | "locked";

const GATE_LABEL: Record<Exclude<Gate, "open">, string> = {
  signin: "Sign in",
  // Names where the deck is available without selling it: same reasoning as
  // the tier's what's-included list. No button, no link, no checkout.
  locked: "Included with Professional",
};

const DeckCard: React.FC<{
  deck: CourseDeck;
  gate: Gate;
  resolving: boolean;
  onGated: (deck: CourseDeck, gate: Gate) => void;
  onDownload: (deck: CourseDeck) => void;
}> = ({ deck, gate, resolving, onGated, onDownload }) => {
  const open = gate === "open";

  // Page count is always slides + 1, so printing both spends two numbers on
  // one fact. Minutes answers a question the reader actually has; topic says
  // what the chapter is about, which the title alone does not.
  const facts = [`${deck.slides} slides`, `${deck.minutes} min`];

  const body = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-accent">
          Chapter {deck.chapter}
        </span>
        {deck.tier === "public" ? (
          <span className="rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-accent">
            Free
          </span>
        ) : (
          !open && (
            <Lock className="h-3.5 w-3.5 shrink-0 self-center text-text-secondary/40" />
          )
        )}
      </div>
      <h3 className="mt-1.5 font-semibold leading-snug text-text-primary">
        {deck.title}
      </h3>
      <p className="mt-1 text-sm text-text-secondary">{deck.topic}</p>
      <p className="mt-1 pb-3 text-xs text-text-secondary/70">
        {facts.join(" · ")}
      </p>
    </>
  );

  /**
   * The action row. `mt-auto` pushes it to the bottom of the card and the grid
   * stretches every card to the tallest in its row, so actions line up across
   * the row instead of floating wherever the title happened to end.
   */
  const actionRow =
    "mt-auto flex items-center justify-center gap-1.5 border-t border-border-primary/60 pt-3 text-sm font-semibold";

  const shell =
    "flex h-full flex-col rounded-lg border border-border-primary p-4 text-left transition-colors";

  // Until entitlement is known, show no CTA rather than the wrong one: a
  // paying customer must never be told to "See plans" for a deck they own.
  if (!open && resolving) {
    return (
      <div className={`${shell} opacity-60`}>
        {body}
        <span className={actionRow}>
          <span className="h-4 w-20 animate-pulse rounded bg-border-primary" />
        </span>
      </div>
    );
  }

  // No per-card gate text in either locked branch: the status line above the
  // grid already says who gets what, and repeating it on fourteen cards turns
  // the page into the wall of locks that line exists to prevent. The lock
  // glyph in the header carries the state.
  if (gate === "locked") {
    return <div className={`${shell} opacity-70`}>{body}</div>;
  }

  // Signed-out: the whole card is the target, so one click still opens
  // sign-in with no dead end. aria-label carries the action for screen
  // readers now that the visible row is gone.
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => onGated(deck, gate)}
        aria-label={`${deck.title} — ${GATE_LABEL[gate].toLowerCase()} to download`}
        className={`${shell} group w-full hover:border-accent hover:bg-bg-secondary`}
      >
        {body}
      </button>
    );
  }

  return (
    <div className={`${shell} hover:border-accent`}>
      {body}
      <button
        type="button"
        onClick={() => onDownload(deck)}
className={`${actionRow} text-accent hover:underline`}
      >
        <FileDown className="h-4 w-4" /> Download PDF
      </button>
    </div>
  );
};

const CourseLibrary: React.FC<CourseLibraryProps> = ({
  user,
  authLoading = false,
}) => {
  const { t: tCommon } = useTranslation("common");
  const [tier, setTier] = React.useState<string>("none");
  const [loading, setLoading] = React.useState<boolean>(true);
  const [showLogin, setShowLogin] = React.useState(false);
  const [pendingDeck, setPendingDeck] = React.useState<CourseDeck | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const signedIn = !!user?.userID;

  /**
   * `silent` re-reads the tier without flipping the loading flag, so a poll
   * doesn't pulse every row in the catalog while it waits.
   */
  const refreshTier = React.useCallback(
    async (silent = false): Promise<string> => {
      if (!silent) setLoading(true);
      try {
        const profile = await apiService.getCustomerProfile();
        const next = profile?.tier || "none";
        setTier(next);
        return next;
      } catch {
        setTier("none");
        return "none";
      } finally {
        if (!silent) setLoading(false);
      }
    },
    []
  );

  React.useEffect(() => {
    if (!signedIn) {
      setLoading(false);
      setTier("none");
      return;
    }
    refreshTier();
  }, [signedIn, refreshTier]);

  const hasCourse = React.useMemo(() => hasCourseAccess(tier), [tier]);

  /** True while either the session or the tier behind it is still unknown. */
  const resolving = authLoading || loading;

  const gateFor = React.useCallback(
    (deck: CourseDeck): Gate => {
      if (deck.tier === "public" || hasCourse) return "open";
      return signedIn ? "locked" : "signin";
    },
    [hasCourse, signedIn]
  );

  /** Route a locked click. Sign-in is the only flow this page offers. */
  const act = React.useCallback((gate: Gate) => {
    if (gate === "signin") setShowLogin(true);
  }, []);

  /**
   * Free decks are public objects; paid decks go through the same signed-URL
   * path as the book downloads (`/api/downloads/:id/url`), using deterministic
   * download IDs so no extra lookup is needed.
   */
  const handleDownload = React.useCallback(async (deck: CourseDeck) => {
    setError(null);
    if (deck.publicUrl) {
      window.location.href = deck.publicUrl;
      return;
    }
    try {
      const url = await apiService.getDownloadUrl(`course-${deck.id}`);
      window.location.href = url;
    } catch (e) {
      console.error("Failed to get course download URL:", e);
      setError(`Could not open "${deck.title}". Please try again.`);
    }
  }, []);

  const handleGated = (deck: CourseDeck, gate: Gate) => {
    setPendingDeck(deck);
    act(gate);
  };

  // Resume the deck the user was reaching for once access actually lands,
  // so signing in or upgrading finishes the job instead of dumping them back
  // on the same list.
  React.useEffect(() => {
    if (!pendingDeck || resolving || !hasCourse) return;
    const deck = pendingDeck;
    setPendingDeck(null);
    handleDownload(deck);
  }, [pendingDeck, resolving, hasCourse, handleDownload]);

  const lockedCount = courseDecks.filter((d) => gateFor(d) !== "open").length;

  const bannerGate: Exclude<Gate, "open"> = signedIn ? "locked" : "signin";

  return (
    <div className="w-full py-10">
      <div className="text-xs font-bold uppercase tracking-widest text-accent">
        The Course
      </div>
      <h1 className="mt-1 text-3xl font-bold text-text-primary md:text-4xl">
        Slides for every chapter
      </h1>
      <p className="mt-3 max-w-3xl text-lg font-light text-text-secondary">
        Each chapter of the book as a set of presentation slides, downloadable
        as PDF. Chapters 1 and 2 are free.
      </p>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-text-secondary">
        <span>
          <b className="text-text-primary">{courseTotals.decks}</b> chapters
        </span>
        <span>
          <b className="text-text-primary">{courseTotals.slides}</b> slides
        </span>
        <span>
          <b className="text-text-primary">{courseTotals.demos}</b> runnable
          demos
        </span>
      </div>

      {/* One status line — never a wall of locks */}
      {!resolving && hasCourse && (
        <div className="mt-6 flex items-center gap-2 rounded-lg border border-accent/30 bg-accent/5 px-4 py-2.5 text-sm text-text-primary">
          <Check className="h-4 w-4 text-accent" />
          Your plan includes all {courseTotals.decks} chapters.
        </div>
      )}
      {!resolving && !hasCourse && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border-primary bg-bg-secondary px-4 py-3 text-sm">
          <span className="text-text-secondary">
            Chapters 1 and 2 are free to download. The rest are included with
            Professional.
          </span>
          {!signedIn && (
            <button
              type="button"
              onClick={() => act(bannerGate)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 font-semibold text-white"
            >
              {GATE_LABEL[bannerGate]}
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-lg border border-red-300 bg-red-50 px-4 py-2.5 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {/* The catalog — everything visible, always */}
      <div className="mt-8">
        {coursePartOrder.map((part) => {
          const decks = courseDecks.filter((d) => d.part === part);
          return (
            <section key={part} className="mb-10">
              <h2 className="flex items-baseline justify-between border-b-2 border-accent pb-1 text-xs font-bold uppercase tracking-widest text-accent">
                {part}
                <span className="font-semibold text-text-secondary">
                  {decks.length} chapters
                </span>
              </h2>
              {/* Three across is the ceiling: the app is capped at 1400px, so a
    fourth column leaves ~300px per card and long chapter titles wrap
    to three lines. */}
              <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {decks.map((d) => (
                  <DeckCard
                    key={d.id}
                    deck={d}
                    gate={gateFor(d)}
                    resolving={resolving}
                    onGated={handleGated}
                    onDownload={handleDownload}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Sign-in — the dashboard's modal + providers, reused */}
      {showLogin && (
        <Modal
          isOpen={showLogin}
          onClose={() => setShowLogin(false)}
          title={tCommon("auth.signIn")}
          size="md"
        >
          <div className="p-6">
            {pendingDeck && (
              <p className="mb-4 text-sm text-text-secondary">
                Sign in to open{" "}
                <b className="text-text-primary">{pendingDeck.title}</b> and the
                rest of the course.
              </p>
            )}
            <AuthProviders
              onSuccess={() => setShowLogin(false)}
              onCancel={() => setShowLogin(false)}
              showCancel={true}
              inline={true}
              description={tCommon("auth.signInToAccess")}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CourseLibrary;
